import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserPlus, Trash2, Save, Upload, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SecuritySection from '../components/SecuritySection';
import BrandingSection from '../components/BrandingSection';
import SharePreviewSection from '../components/SharePreviewSection';
import TenantsSection from '../components/TenantsSection';
import DangerZone from '../components/DangerZone';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');

export default function Settings() {
  const { user, company, refreshMe } = useAuth();
  const [members, setMembers] = useState([]);
  const [branding, setBranding] = useState(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'MEMBER' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const loadMembers = async () => {
    try {
      const { data } = await api.get('/users/members');
      setMembers(data);
    } catch {
      toast.error('Erreur');
    }
  };

  useEffect(() => { loadMembers(); }, []);

  useEffect(() => {
    if (company && !branding) {
      setBranding({
        name: company.name || '',
        tagline: company.tagline || '',
        description: company.description || '',
        phone: company.phone || '',
        email: company.email || '',
        address: company.address || '',
        website: company.website || '',
        color: company.color || '#2563eb',
        heroBgColor: company.heroBgColor || '',
        pageBgColor: company.pageBgColor || '',
      });
    }
  }, [company, branding]);

  const setB = (field) => (e) => setBranding({ ...branding, [field]: e.target.value });

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.put('/users/company', branding);
      await refreshMe();
      toast.success('Personnalisation enregistrée');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setSavingBranding(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    try {
      await api.post('/users/company/logo', fd);
      await refreshMe();
      toast.success('Logo mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      e.target.value = '';
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/members', newMember);
      setShowAdd(false);
      setNewMember({ name: '', email: '', password: '', role: 'MEMBER' });
      loadMembers();
      toast.success('Membre ajouté');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    try {
      await api.delete(`/users/members/${id}`);
      loadMembers();
      toast.success('Membre supprimé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const changePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (pwForm.newPassword.length < 8) return toast.error('8 caractères minimum');
    try {
      await api.put('/users/password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      toast.success('Mot de passe modifié');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="container" style={{ padding: '24px 16px', maxWidth: 700 }}>
      <h2 style={{ marginBottom: 24 }}>Réglages</h2>

      {isAdmin && branding && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <h3>Personnalisation de la page publique</h3>
            {company?.slug && (
              <a href={`/e/${company.slug}`} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Voir ma page <ExternalLink size={13} />
              </a>
            )}
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 16 }}>
            Ces informations apparaissent sur votre vitrine publique et vos pages chantier.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            {company?.logo ? (
              <img src={`${API_BASE}/uploads/${company.logo}`} alt="Logo"
                style={{ height: 56, width: 'auto', borderRadius: 6, background: '#f8fafc', padding: 4 }} />
            ) : (
              <div style={{
                height: 56, width: 56, borderRadius: 6, background: '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8', fontSize: '0.7rem',
              }}>Logo</div>
            )}
            <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
              <Upload size={14} /> {company?.logo ? 'Changer le logo' : 'Ajouter un logo'}
              <input type="file" accept="image/*" onChange={uploadLogo} style={{ display: 'none' }} />
            </label>
          </div>

          <div className="form-group">
            <label>Nom de l'entreprise</label>
            <input value={branding.name} onChange={setB('name')} />
          </div>
          <div className="form-group">
            <label>Slogan (tagline)</label>
            <input value={branding.tagline} onChange={setB('tagline')}
              placeholder="Ex. Ravalement · Pierre de taille · Peinture" />
          </div>
          <div className="form-group">
            <label>Présentation</label>
            <textarea rows={4} value={branding.description} onChange={setB('description')}
              placeholder="Décrivez votre activité, vos spécialités..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Téléphone</label>
              <input value={branding.phone} onChange={setB('phone')} />
            </div>
            <div className="form-group">
              <label>Email public</label>
              <input type="email" value={branding.email} onChange={setB('email')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Adresse</label>
              <input value={branding.address} onChange={setB('address')} />
            </div>
            <div className="form-group">
              <label>Site web</label>
              <input value={branding.website} onChange={setB('website')} placeholder="https://..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Couleur principale</label>
              <input type="color" value={branding.color} onChange={setB('color')}
                style={{ height: 38, padding: 2, width: '100%' }} />
            </div>
            <div className="form-group">
              <label>Fond de l'en-tête</label>
              <input type="color" value={branding.heroBgColor || '#f8fafc'} onChange={setB('heroBgColor')}
                style={{ height: 38, padding: 2, width: '100%' }} />
            </div>
            <div className="form-group">
              <label>Fond de page</label>
              <input type="color" value={branding.pageBgColor || '#f8fafc'} onChange={setB('pageBgColor')}
                style={{ height: 38, padding: 2, width: '100%' }} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={saveBranding} disabled={savingBranding}>
            <Save size={16} /> {savingBranding ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Membres ({members.length})</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
              <UserPlus size={14} /> Ajouter
            </button>
          </div>

          {members.map(m => (
            <div key={m.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: '1px solid var(--border)',
            }}>
              <div>
                <strong>{m.name}</strong>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginLeft: 8 }}>{m.email}</span>
                <span className={`badge ${m.role === 'ADMIN' ? 'badge-progress' : 'badge-not-started'}`}
                  style={{ marginLeft: 8 }}>
                  {m.role === 'ADMIN' ? 'Admin' : 'Membre'}
                </span>
              </div>
              {m.id !== user.id && (
                <button className="btn btn-danger btn-sm" onClick={() => removeMember(m.id)}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Mot de passe</h3>
        <div className="form-group">
          <label>Mot de passe actuel</label>
          <input type="password" value={pwForm.currentPassword}
            onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Nouveau mot de passe</label>
            <input type="password" value={pwForm.newPassword} minLength={8}
              onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Confirmer</label>
            <input type="password" value={pwForm.confirm}
              onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={changePassword}>
          <Save size={16} /> Changer le mot de passe
        </button>
      </div>

      <BrandingSection />

      <SharePreviewSection />

      <SecuritySection />

      <TenantsSection />

      <DangerZone />

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Ajouter un membre</h2>
            <form onSubmit={addMember}>
              <div className="form-group">
                <label>Nom</label>
                <input value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input type="password" value={newMember.password} onChange={e => setNewMember({ ...newMember, password: e.target.value })} required minLength={8} />
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select value={newMember.role} onChange={e => setNewMember({ ...newMember, role: e.target.value })}>
                  <option value="MEMBER">Membre</option>
                  <option value="ADMIN">Administrateur</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setShowAdd(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
